/**
 * @module spa/helpers
 */

let wrapper = null;

/**
 * @returns {HTMLElement} page wrapper
 */
export const getWrapper = () => {
    if (wrapper !== null) {
        return wrapper;
    }

    wrapper = document.querySelector('#wrapper');

    return wrapper;
};

/**
 * @param {Array<Object>} sizeArray - array of sizes to sort
 * @returns {Array<Object>} sorted sizes
 */
const sortSizeBySizeName = sizeArray => {
    const annotatedSizeArray = [];
    const sizes = [];

    // We add the normalized size as an attribute to the objects so we can easily sort it afterwards
    sizeArray.forEach(entry => {
        // Generate a normalized size and annotate it at the entry
        let normalizedSize = entry.size.replace(',', '.').split('-');
        normalizedSize = parseFloat(normalizedSize);

        entry.normalizedSize = normalizedSize;

        // Skip duplicates and empty values
        if (!normalizedSize || sizes.indexOf(normalizedSize) !== -1) {
            return;
        }

        // Store normalized size in list so we can find duplicates
        sizes.push(normalizedSize);

        // Add the entry to the extended list
        annotatedSizeArray.push(entry);
    });

    // Sort by size ascending
    annotatedSizeArray.sort((entry1, entry2) => entry1.normalizedSize - entry2.normalizedSize);

    return annotatedSizeArray;
};

/**
 * @param {Array<Object>} sizeArray - array of sizes to sort
 * @returns {Array<Object>} sorted sizes
 */
const sortSizeByAltSizeName = sizeArray => {
    const annotatedSizeArray = [];
    const sizes = [];

    sizeArray.forEach(entry => {
        let normalizedSize = entry.alt_size.replace(',', '.').split('-');
        normalizedSize = parseFloat(normalizedSize);
        entry.normalizedSize = normalizedSize;

        if (!normalizedSize || sizes.indexOf(normalizedSize) !== -1) {
            return;
        }
        sizes.push(normalizedSize);
        annotatedSizeArray.push(entry);
    });
    annotatedSizeArray.sort((entry1, entry2) => entry1.normalizedSize - entry2.normalizedSize);
    return annotatedSizeArray;
};

/**
 * @returns {{mainSize: string, alternativeSize: string}} Main and alternative size regions depending on the current side
 */
const getSizeRegionNames = () => {
    let mainSize;
    let alternativeSize;
    switch (window.Constants.SITE_ID) {
        case 'US':
            mainSize = 'US';
            alternativeSize = 'EU';
            break;
        case 'CA':
            mainSize = 'EU';
            alternativeSize = 'US';
            break;
        case 'GB':
            mainSize = 'UK';
            alternativeSize = 'EU';
            break;
        case 'JP':
            mainSize = 'JP';
            alternativeSize = 'EU';
            break;
        default:
            mainSize = 'EU';
            alternativeSize = 'UK';
    }

    return [mainSize, alternativeSize];
};

/**
 * @returns {Object|null} parsed JSON refinements configuration or `null`
 */
const getRefinementsConfiguration = () => {
    let refinementsConfig = null;
    try {
        refinementsConfig = JSON.parse(window.SitePreferences.REFINEMENT_CONFIGURATION);
    } catch (error) {
        console.error(error);
    }

    return refinementsConfig;
};

/**
 * @returns {string} the URL country path which can be used for URL building.
 * Does not support internal (non-pretty) URLs.
 */
export const getCountryPath = () => {
    const pathName = window.location.pathname.split('/');
    return pathName[1] === 's' ? `/${pathName[1]}/${pathName[2]}` : `/${pathName[1]}`;
};

/**
 * @param {Array<Object>} refinementValues - size values to build size map based of
 * @returns {Array<import('./type-definitions').SizeRegion>} size map
 */
export const generateSizeMap = refinementValues => {
    let tab1 = [];
    let tab2 = [];
    const {searchParams} = new URL(window.location.href);
    const [mainSize, alternativeSize] = getSizeRegionNames();
    let selectedRegion = searchParams.get('region');
    if (selectedRegion !== mainSize && selectedRegion !== alternativeSize) {
        selectedRegion = mainSize;
    }
    for (const refinementValue of refinementValues) {
        const sizes = refinementValue.name.split(';');

        if (sizes.length === 1) {
            tab1.push({...refinementValue, size: sizes[0]});
            tab2.push({...refinementValue, size: sizes[0]});
        } else {
            tab1.push({...refinementValue, size: sizes[0], alt_size: sizes[1]});
            tab2.push({...refinementValue, size: sizes[1], alt_size: sizes[0]});
        }
    }

    if (window.SitePreferences.ENABLE_SORTING_BY_EU_SIZE) {
        tab1 = sortSizeByAltSizeName(tab1);
    } else {
        tab1 = sortSizeBySizeName(tab1);
    }
    tab2 = sortSizeBySizeName(tab2);

    const regionSizes = [
        {
            name: mainSize,
            selected: mainSize === selectedRegion,
            values: tab1
        },
        {
            name: alternativeSize,
            selected: alternativeSize === selectedRegion,
            values: tab2
        }
    ];

    return regionSizes;
};

/**
 * @param {number} genderId - gender id
 * @param {Object} [category] - category object
 * @returns {string} gender attribute
 */
export const mapGenderAttribute = (genderId, category) => {
    let value = 'woman';
    if (genderId === 4) {
        value = 'man';
    }

    // unixes applicable for both categories
    if (genderId === 5 && category) {
        const categoryGender = category.data.gender;
        if (categoryGender !== '') {
            value = categoryGender;
        }
    }

    return value;
};

/**
 * @param {string} id - ID as string to cleanup
 * @returns {string} cleaned id
 */
export const cleanupID = id => (id === null ? id : id.replace(/[^a-z0-9_-]/gi, '_').toLowerCase());

/**
 * @param {string} width - width attribute
 * @returns {'n'|'s'} width type
 */
export const getWidthType = width => {
    const lowerCaseWidth = width.toLowerCase();
    if (lowerCaseWidth.includes('normal') || lowerCaseWidth.includes('m') || lowerCaseWidth.includes('n')) {
        return 'n';
    }
    return 's';
};

/**
 * @param {string} refinementId - refinement id. Must be stripped of the `_{countryCode}` part
 * @returns {string} expanded class
 */
export const getRefinementExpandClass = refinementId => {
    const EXPANDED = 'expanded';
    const refinementsConfig = getRefinementsConfiguration();
    let expandedClass = '';
    if (refinementsConfig && refinementsConfig[refinementId]) {
        if (
            refinementsConfig[refinementId].mobile === EXPANDED &&
            refinementsConfig[refinementId].desktop === EXPANDED
        ) {
            expandedClass = 'expanded';
        } else if (refinementsConfig[refinementId].mobile === EXPANDED) {
            expandedClass = 'expanded-mobile expanded';
        } else if (refinementsConfig[refinementId].desktop === EXPANDED) {
            expandedClass = 'expanded-desktop expanded';
        }
    }

    return expandedClass;
};

/**
 * @param {string} refinementId - refinement id. Must be stripped of the `_{countryCode}` part
 * @returns {string} cut off threshold
 */
export const getRefinementCutOffThreshold = refinementId => {
    const refinementsConfig = getRefinementsConfiguration();
    let threshold = 6;
    if (refinementsConfig) {
        if (refinementsConfig[refinementId]?.cutOffThreshold) {
            threshold = refinementsConfig[refinementId].cutOffThreshold;
        } else if (refinementsConfig.defaultCutOffThreshold) {
            threshold = refinementsConfig.defaultCutOffThreshold;
        }
    }

    return threshold;
};

/**
 * @returns {Array<Object>} array of promotional rows to display
 */
export const getPromoRows = () => {
    const stringifiedPromoRows = getWrapper()?.dataset?.stringifiedPromoRows;
    if (stringifiedPromoRows) {
        try {
            return JSON.parse(stringifiedPromoRows);
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    return [];
};

/**
 * @returns {Array<Object>} array of promotional tiles to display alongside the product tiles
 */
export const getPromoTiles = () => {
    const stringifiedPromoTiles = getWrapper()?.dataset?.stringifiedPromoTiles;
    if (stringifiedPromoTiles) {
        try {
            return JSON.parse(stringifiedPromoTiles);
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    return [];
};

/**
 * @param {string} baseURL - base relative URL from the Constructor response that is missing the current market
 * @returns {string} - relative URL for the current shop
 */
export const buildRelativeURL = baseURL => {
    if (!baseURL) {
        return '';
    }
    const countryPath = getCountryPath();
    return `${countryPath}${baseURL}`;
};

/**
 * @param {Object} config - SEO exclude configuration
 * @param {Object} renderedFilterObject - SEO exclude configuration
 * @returns {boolean} - Result of check
 */
export const checkIfLinkShouldNotBeMasked = (config, renderedFilterObject) => {
    let result = false;

    if (!config) {
        return result;
    }

    const appliedRefinements = [];
    const matchedReminements = [];
    const params = new URLSearchParams(window.location.search);

    for (const [key, value] of params.entries()) {
        if (key.includes('filters')) {
            const name = key.replace('filters[', '').replace(']', '');
            const normalizedValue = value.replace(/\\"/g, '').toLowerCase();
            const sanitizedValue = normalizedValue.includes(',') ? normalizedValue.split(',') : normalizedValue;

            appliedRefinements.push({
                name,
                value: sanitizedValue
            });
        }
    }

    appliedRefinements.forEach(param => {
        const {name, value} = param;
        const excludeRefinement = config.find(refinement => refinement.name === name);

        if (excludeRefinement && (value.includes(excludeRefinement.value) || excludeRefinement.value === '*')) {
            matchedReminements.push(excludeRefinement);
        }
    });

    if (matchedReminements.length >= 2) {
        return result;
    }

    result = Boolean(
        config.find(
            refinement =>
                refinement.name === renderedFilterObject.name &&
                (refinement.value === renderedFilterObject.value.toLowerCase() || refinement.value === '*')
        )
    );

    return result;
};
